import { type ReactNode, useLayoutEffect } from 'react';

import { useLocalStorage, useMediaQuery } from '@lib/hooks';
import { ThemeContext, type ThemeContextValue } from './ThemeContext';
import type { CardSkin, ThemeConfig, ThemeMode, ThemePreset } from '@lib/types/theme';

// Default theme configuration
const defaultTheme: ThemeConfig = {
  themeMode: 'system',
  cardSkin: 'shadow',
  preset: 'indigo',
};

const initialState: ThemeContextValue = {
  ...defaultTheme,
  setThemeMode: () => {},
  setPreset: () => {},
  setCardSkin: () => {},
  resetTheme: () => {},
  isDark: false,
};

const COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const getHtml = () => (typeof document !== 'undefined' ? document.documentElement : null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const isDarkOS = useMediaQuery(COLOR_SCHEME_QUERY);

  const [settings, setSettings] = useLocalStorage<ThemeConfig>('theme-settings', {
    themeMode: initialState.themeMode,
    cardSkin: initialState.cardSkin,
    preset: initialState.preset,
  });

  const isDark = (settings.themeMode === 'system' && isDarkOS) || settings.themeMode === 'dark';

  const setThemeMode = (val: ThemeMode) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      themeMode: val,
    }));
  };

  const setPreset = (val: ThemePreset) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      preset: val,
    }));
  };

  const setCardSkin = (val: CardSkin) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      cardSkin: val,
    }));
  };

  const resetTheme = () => {
    setSettings({
      themeMode: initialState.themeMode,
      preset: initialState.preset,
      cardSkin: initialState.cardSkin,
    });
  };

  // Apply dark mode class
  useLayoutEffect(() => {
    const html = getHtml();
    if (isDark) {
      html?.classList.add('dark');
    } else {
      html?.classList.remove('dark');
    }
  }, [isDark]);

  // Apply preset
  useLayoutEffect(() => {
    const html = getHtml();
    if (html) {
      html.dataset.themePreset = settings.preset;
    }
  }, [settings.preset]);

  // Apply card skin
  useLayoutEffect(() => {
    const html = getHtml();
    if (html) {
      html.dataset.cardSkin = settings.cardSkin;
    }
  }, [settings.cardSkin]);

  if (!children) {
    return null;
  }

  const contextValue: ThemeContextValue = {
    ...settings,
    isDark,
    setThemeMode,
    setPreset,
    setCardSkin,
    resetTheme,
  };

  return <ThemeContext value={contextValue}>{children}</ThemeContext>;
}
